import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useCallback } from "react";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import MuiDrawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import NavbarNEW from "../../../Layout/NavbarNEW";
import { useSelector, useDispatch } from "react-redux";
import { Avatar, Collapse, Menu, MenuItem, Typography } from "@mui/material";
import { capitalizeWords } from "../../../utils/common/capitalize";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { logout } from "../../../reduxStore/actions/auth";
import RandomUser from "../../../assets/icons/male-user.png";
import { axiosConfig } from "../../../axios/axiosConfig";
import toast from "react-hot-toast";



const SIDEBAR_WIDTH = 260;
const SIDEBAR_COLLAPSED_W = 72;

// mainListItems format
// const mainListItems = [
//   {
//     id: 1,
//     label: "Profile",
//     link: "/user-dashboard/profile",
//     icon: <DashboardIcon />,
//   },
//   {
//     id: 2,
//     label: "Payments",
//     link: "/user-dashboard/payments",
//     icon: <PaymentIcon />,
//   },
//   {
//     id: 3,
//     label: "Trading",
//     link: "/user-dashboard/trading",
//     icon: <ShowChartIcon />,
//   },
//   {
//     id: 4,
//     label: "Referrals",
//     link: "/user-dashboard/referrals",
//     icon: <Diversity3Icon />,
//   },

//   {
//     id: 5,
//     label: "Support",
//     link: "/user-dashboard/support  ",
//     icon: <EmailIcon />,
//   },
// ];

// const AppBar = styled(MuiAppBar, {
//   shouldForwardProp: (prop) => prop !== "open",
// })(({ theme, open }) => ({
//   zIndex: theme.zIndex.drawer + 1,
//   transition: theme.transitions.create(["width", "margin"], {
//     easing: theme.transitions.easing.sharp,
//     duration: theme.transitions.duration.leavingScreen,
//   }),
//   ...(open && {
//     marginLeft: drawerWidth,
//     width: `calc(100% - ${drawerWidth}px)`,
//     transition: theme.transitions.create(["width", "margin"], {
//       easing: theme.transitions.easing.sharp,
//       duration: theme.transitions.duration.enteringScreen,
//     }),
//   }),
// }));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: SIDEBAR_WIDTH,
    height: '100%',
    background: '#04343a',
    color: '#B8D4C4',
    borderRight: 'none',
    overflowX: 'hidden',
    overflowY: 'auto',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
    ...(!open && {
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: SIDEBAR_COLLAPSED_W,
    }),
  },
}));

const mdTheme = createTheme({ palette: { primary: { main: '#016551' } } });

const navItemSx = {
  borderRadius: '8px', mx: '8px', my: '2px', px: '12px', py: '8px',
  color: '#B8D4C4', transition: 'all 150ms ease',
  '&:hover': { backgroundColor: 'rgba(1,101,81,0.12)', color: '#FFFFFF', '& .MuiListItemIcon-root': { color: '#FFFFFF' } },
  '&.Mui-selected': { backgroundColor: '#016551', color: '#FFFFFF', '& .MuiListItemIcon-root': { color: '#FFFFFF' }, '&:hover': { backgroundColor: '#014d3d' } },
};
const iconSx = { minWidth: 36, color: '#ffffff', transition: 'color 150ms ease' };
const sectionLabelSx = {
  px: '20px', pt: '16px', pb: '4px',
  fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em',
  textTransform: 'uppercase', color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden',
};

export default function DashboardLayout({
  mainListItems,
  secondListItems = [],
  hideNavbar = false,
}) {
  const {
    user: { userDetails },
  } = useSelector((store) => store.auth);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const [openNested, setOpenNested] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);

  const handleLogOut = useCallback(async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      const user_id = userData?.response?.id || userData?.userDetails?.id;
      if (user_id) {
        await axiosConfig({ method: "post", url: "/enroll/user-logout", data: { user_id, device_id: "web-client" } });
      }
      dispatch(logout());
      localStorage.clear();
      setAnchorEl(null);
      navigate("/login");
    } catch {
      dispatch(logout());
      localStorage.clear();
      navigate("/login");
    }
  }, [dispatch, navigate]);
  const toggleDrawer = () => {
    setOpen(!open);
  };
  const handleToggle = (key) => {
    setOpenNested((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };
  return (
    <ThemeProvider theme={mdTheme}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      {!(hideNavbar && userDetails?.user_type?.toLowerCase() === "investor") && (
        <NavbarNEW isAuth={!!userDetails} userDetails={userDetails} />
      )}

      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
        <CssBaseline />

        <Drawer variant="permanent" open={open}>
          {/* Sidebar Header */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: open ? "space-between" : "center", mx: "8px", px: open ? "12px" : 0, py: 1.5, minHeight: 56, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
            {open && (
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#ffffff", letterSpacing: "0.05em", textTransform: "uppercase", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>
                {capitalizeWords(userDetails?.username || "Dashboard")}
              </Typography>
            )}
            <IconButton onClick={toggleDrawer} size="small" sx={{ color: "#3D6350", "&:hover": { color: "#B8D4C4", background: "rgba(255,255,255,0.07)" } }}>
              {open ? <ChevronLeftIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
            </IconButton>
          </Box>

          {/* Nav Items */}
          <Box sx={{ overflowY: "auto", overflowX: "hidden", flexGrow: 1, py: 1 }}>
            {open && <Typography sx={sectionLabelSx}>Main Menu</Typography>}
            <List component="nav" disablePadding>
              {mainListItems.map((list) => {
                const hasChildren = Array.isArray(list.children);
                const nestedKey = `main-${list.id}`;
                const isExpanded = openNested[nestedKey] ?? false;
                const isSelected = !hasChildren && location.pathname === list.link;
                return (
                  <div key={list.id}>
                    <Tooltip title={!open ? list.label : ""} placement="right" arrow>
                      <ListItemButton sx={navItemSx} selected={isSelected} onClick={() => { if (hasChildren) handleToggle(nestedKey); else navigate(list.link); }}>
                        <ListItemIcon sx={iconSx}>{list.icon}</ListItemIcon>
                        {open && (
                          <>
                            <ListItemText primary={<span style={{ fontSize: "0.875rem", fontWeight: isSelected ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{list.label}</span>} />
                            {hasChildren ? (isExpanded ? <ExpandLess sx={{ fontSize: 16, color: "#4A6A55" }} /> : <ExpandMore sx={{ fontSize: 16, color: "#4A6A55" }} />) : null}
                          </>
                        )}
                      </ListItemButton>
                    </Tooltip>
                    {hasChildren && open && (
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {list.children.map((child) => {
                            const childHasChildren = Array.isArray(child.children);
                            const childNestedKey = `main-${list.id}-${child.id}`;
                            const childExpanded = openNested[childNestedKey] ?? false;
                            const cs = !childHasChildren && location.pathname === child.link;
                            return (
                              <div key={child.id}>
                                <ListItemButton
                                  sx={{ ...navItemSx, pl: "32px", py: "6px", ...(cs && { backgroundColor: "#016551", color: "#FFFFFF", "& .MuiListItemIcon-root": { color: "#FFFFFF" }, "&:hover": { backgroundColor: "#014d3d" } }) }}
                                  onClick={() => childHasChildren ? handleToggle(childNestedKey) : navigate(child.link)}
                                >
                                  <ListItemIcon sx={{ ...iconSx, minWidth: 28 }}>{child.icon}</ListItemIcon>
                                  <ListItemText primary={<span style={{ fontSize: "0.8125rem", fontWeight: cs ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{child.label}</span>} />
                                  {childHasChildren ? (childExpanded ? <ExpandLess sx={{ fontSize: 14, color: "#4A6A55" }} /> : <ExpandMore sx={{ fontSize: 14, color: "#4A6A55" }} />) : null}
                                </ListItemButton>
                                {childHasChildren && (
                                  <Collapse in={childExpanded} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                      {child.children.map((grandchild) => {
                                        const gcs = location.pathname === grandchild.link;
                                        return (
                                          <ListItemButton
                                            key={grandchild.id}
                                            sx={{ ...navItemSx, pl: "48px", py: "5px", ...(gcs && { backgroundColor: "#016551", color: "#FFFFFF", "& .MuiListItemIcon-root": { color: "#FFFFFF" }, "&:hover": { backgroundColor: "#014d3d" } }) }}
                                            onClick={() => navigate(grandchild.link)}
                                          >
                                            <ListItemIcon sx={{ ...iconSx, minWidth: 24 }}>{grandchild.icon}</ListItemIcon>
                                            <ListItemText primary={<span style={{ fontSize: "0.775rem", fontWeight: gcs ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{grandchild.label}</span>} />
                                          </ListItemButton>
                                        );
                                      })}
                                    </List>
                                  </Collapse>
                                )}
                              </div>
                            );
                          })}
                        </List>
                      </Collapse>
                    )}
                  </div>
                );
              })}
            </List>

            {secondListItems.length > 0 && (
              <>
                <Box sx={{ mx: 2, my: 1.5, borderTop: "1px solid rgba(255,255,255,0.08)" }} />
                {open && <Typography sx={sectionLabelSx}>Settings</Typography>}
                <List component="nav" disablePadding>
                  {secondListItems.map((list) => {
                    const hasChildren = Array.isArray(list.children);
                    const nestedKey = `secondary-${list.id}`;
                    const isExpanded = openNested[nestedKey] ?? false;
                    const isSelected = !hasChildren && location.pathname === list.link;
                    return (
                      <div key={list.id}>
                        <Tooltip title={!open ? list.label : ""} placement="right" arrow>
                          <ListItemButton sx={navItemSx} selected={isSelected} onClick={() => { if (hasChildren) handleToggle(nestedKey); else navigate(list.link); }}>
                            <ListItemIcon sx={iconSx}>{list.icon}</ListItemIcon>
                            {open && (
                              <>
                                <ListItemText primary={<span style={{ fontSize: "0.875rem", fontWeight: isSelected ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{list.label}</span>} />
                                {hasChildren ? (isExpanded ? <ExpandLess sx={{ fontSize: 16, color: "#4A6A55" }} /> : <ExpandMore sx={{ fontSize: 16, color: "#4A6A55" }} />) : null}
                              </>
                            )}
                          </ListItemButton>
                        </Tooltip>
                        {hasChildren && open && (
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                              {list.children.map((child) => {
                                const childHasChildren = Array.isArray(child.children);
                                const childNestedKey = `secondary-${list.id}-${child.id}`;
                                const childExpanded = openNested[childNestedKey] ?? false;
                                const cs = !childHasChildren && location.pathname === child.link;
                                return (
                                  <div key={child.id}>
                                    <ListItemButton
                                      sx={{ ...navItemSx, pl: "32px", py: "6px", ...(cs && { backgroundColor: "#016551", color: "#FFFFFF", "& .MuiListItemIcon-root": { color: "#FFFFFF" }, "&:hover": { backgroundColor: "#014d3d" } }) }}
                                      onClick={() => childHasChildren ? handleToggle(childNestedKey) : navigate(child.link)}
                                    >
                                      <ListItemIcon sx={{ ...iconSx, minWidth: 28 }}>{child.icon}</ListItemIcon>
                                      <ListItemText primary={<span style={{ fontSize: "0.8125rem", fontWeight: cs ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{child.label}</span>} />
                                      {childHasChildren ? (childExpanded ? <ExpandLess sx={{ fontSize: 14, color: "#4A6A55" }} /> : <ExpandMore sx={{ fontSize: 14, color: "#4A6A55" }} />) : null}
                                    </ListItemButton>
                                    {childHasChildren && (
                                      <Collapse in={childExpanded} timeout="auto" unmountOnExit>
                                        <List component="div" disablePadding>
                                          {child.children.map((grandchild) => {
                                            const gcs = location.pathname === grandchild.link;
                                            return (
                                              <ListItemButton
                                                key={grandchild.id}
                                                sx={{ ...navItemSx, pl: "48px", py: "5px", ...(gcs && { backgroundColor: "#016551", color: "#FFFFFF", "& .MuiListItemIcon-root": { color: "#FFFFFF" }, "&:hover": { backgroundColor: "#014d3d" } }) }}
                                                onClick={() => navigate(grandchild.link)}
                                              >
                                                <ListItemIcon sx={{ ...iconSx, minWidth: 24 }}>{grandchild.icon}</ListItemIcon>
                                                <ListItemText primary={<span style={{ fontSize: "0.775rem", fontWeight: gcs ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{grandchild.label}</span>} />
                                              </ListItemButton>
                                            );
                                          })}
                                        </List>
                                      </Collapse>
                                    )}
                                  </div>
                                );
                              })}
                            </List>
                          </Collapse>
                        )}
                      </div>
                    );
                  })}
                </List>
              </>
            )}
          </Box>
        </Drawer>

        {/* Main Content */}
        <Box component="main" sx={{ flexGrow: 1, height: "100%", overflowY: "auto", backgroundColor: "#eef2f7" }}>
          <Outlet />
        </Box>
      </Box>
      </Box>
    </ThemeProvider>
  );
}
